"use client";

import { useEffect, useRef } from "react";
import { ArrowRight, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CategoryHero() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = 0;
    let height = 0;
    let particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
    }[] = [];
    const initParticles = () => {
      const isMobile = window.innerWidth < 768;
      const primaryCount = isMobile ? 32 : 80;
      const accentCount = isMobile ? 8 : 12;
      particles = [];
      for (let i = 0; i < primaryCount; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          color: "primary"
        });
      }
      for (let i = 0; i < accentCount; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          color: "accent"
        });
      }
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      initParticles();
    };
    const waitForReady = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        requestAnimationFrame(waitForReady);
        return;
      }
      resize();
      startAnimation();
    };
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            const opacity = (1 - dist / 120) * 0.15;
            ctx.strokeStyle = `hsla(125, 55%, 24%, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const p of particles) {
        ctx.beginPath();
        if (p.color === "primary") {
          ctx.fillStyle = "hsl(125, 55%, 24%)";
          ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
        } else {
          ctx.fillStyle = "hsl(19, 89%, 54%)";
          ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      animationRef.current = requestAnimationFrame(draw);
    };
    const startAnimation = () => {
      animationRef.current = requestAnimationFrame(draw);
    };
    waitForReady();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);
  const tickerItems = ["ESTRUCTURALES", "POLINERÍA", "TUBERÍA", "TREFILADOS", "ESTRUCTURALES", "POLINERÍA", "TUBERÍA", "TREFILADOS"];
  const stats = [{
    value: "40+",
    label: "AÑOS DE EXPERIENCIA"
  }, {
    value: "15",
    label: "CENTROS DE DISTRIBUCIÓN"
  }, {
    value: "100%",
    label: "CALIDAD CERTIFICADA"
  }];
  return <section data-section-id="1730" id="category-hero" className="bg-background text-foreground relative min-h-screen w-full overflow-hidden py-32 md:py-44">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full opacity-40" aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 grid grid-cols-12 gap-6 px-6 md:px-12" aria-hidden="true">
        {Array.from({
        length: 12
      }).map((_, i) => <div key={i} data-index={i} className="h-full border-l" style={{
        borderColor: "hsl(125, 55%, 24%, 0.06)"
      }} />)}
      </div>

      {}
      <div className="pointer-events-none absolute top-0 left-0 h-px w-full" style={{
      backgroundColor: "hsl(125, 55%, 24%, 0.15)"
    }} />

      {}
      <div className="pointer-events-none absolute top-0 right-0 hidden h-full w-8 overflow-hidden border-l lg:block" style={{
      borderColor: "hsl(125, 55%, 24%, 0.15)"
    }}>
        <div className="flex flex-col items-center gap-12 py-4 font-mono text-xs tracking-widest" style={{
        color: "hsl(125, 55%, 24%, 0.5)",
        animation: "vanguard-vertical-scroll 30s linear infinite"
      }}>
          {tickerItems.map((item, i) => <span key={i} data-index={i} className="whitespace-nowrap uppercase" style={{
          writingMode: "vertical-rl"
        }}>
              {item}
            </span>)}
        </div>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:pr-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          {}
          <div ref={leftRef} className="lg:col-span-9" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 500ms cubic-bezier(0.2,0.8,0.2,1), transform 500ms cubic-bezier(0.2,0.8,0.2,1)"
        }}>
            {}
            <div className="mb-10 flex items-center gap-3 font-mono text-[11px] uppercase md:text-[13px]" style={{
            letterSpacing: "0.18em",
            color: "var(--primary)"
          }}>
              <span className="inline-block h-2 w-2" style={{
              backgroundColor: "var(--accent)",
              animation: "vanguard-blink 1.5s steps(2, end) infinite",
              boxShadow: "0 0 12px var(--accent)"
            }} aria-hidden="true" />
              <span>KALISCH_ACERO.V01</span>
            </div>

            {}
            <h1 className="font-default font-bold uppercase" style={{
            fontSize: "clamp(3rem, 8.5vw, 7.5rem)",
            letterSpacing: "-0.04em",
            lineHeight: "0.92",
            color: "var(--foreground)"
          }}>
              FORJAMOS EL FUTURO DE LA{" "}
              <span className="inline-block" style={{
              color: "var(--primary)",
              textShadow: leftVisible ? "0 0 20px hsla(125, 55%, 24%, 0.2)" : "0 0 0px hsla(125, 55%, 24%, 0)",
              transition: "text-shadow 400ms ease-out 200ms"
            }}>
                INDUSTRIA
              </span>
            </h1>

            {}
            <div className="mt-12 flex items-center" aria-hidden="true">
              <div className="h-px flex-1 max-w-[120px]" style={{
              backgroundColor: "var(--primary)"
            }} />
              <div className="h-1.5 w-1.5" style={{
              backgroundColor: "var(--accent)"
            }} />
            </div>

            {}
            <p className="mt-8 max-w-[52ch] font-default text-base md:text-lg" style={{
            color: "var(--muted-foreground)",
            lineHeight: "1.6"
          }}>
              En Kalisch Acero transformamos el acero en soluciones estructurales de alto rendimiento. Desde perfiles comerciales hasta proyectos de infraestructura masiva, garantizamos precisión técnica y suministro ininterrumpido.
            </p>

            {}
            <div className="mt-12 flex flex-col gap-4 sm:flex-row">
              <Button asChild className="font-default font-semibold uppercase tracking-wider" style={{
              backgroundColor: "var(--primary)",
              color: "var(--primary-foreground)",
              borderRadius: "var(--radius)",
              padding: "1rem 2rem",
              height: "auto",
              fontSize: "0.8125rem",
              letterSpacing: "0.15em",
              boxShadow: "var(--shadow-md)"
            }}>
                <Link to="/CatalogPage">
                  EXPLORAR CATÁLOGO
                  <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
                </Link>
              </Button>

              <Button asChild variant="outline" className="font-default font-semibold uppercase tracking-wider bg-transparent" style={{
              borderColor: "var(--primary)",
              borderWidth: "2px",
              color: "var(--primary)",
              borderRadius: "var(--radius)",
              padding: "1rem 2rem",
              height: "auto",
              fontSize: "0.8125rem",
              letterSpacing: "0.15em"
            }}>
                <Link to="/AboutUsPage">NUESTRA HISTORIA</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-3" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 500ms cubic-bezier(0.2,0.8,0.2,1) 200ms, transform 500ms cubic-bezier(0.2,0.8,0.2,1) 200ms"
        }}>
            <div className="flex flex-row gap-3 overflow-x-auto lg:flex-col lg:gap-0">
              {stats.map((stat, i) => <div key={i} data-index={i} className="relative min-w-[180px] flex-shrink-0 px-4 py-6 lg:min-w-0 lg:px-6 lg:py-8" style={{
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              borderBottom: i < stats.length - 1 ? "none" : "1px solid var(--border)"
            }}>
                  {}
                  <span className="absolute top-0 left-0 h-2 w-2 border-t border-l" style={{
                borderColor: "var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute top-0 right-0 h-2 w-2 border-t border-r" style={{
                borderColor: "var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute bottom-0 left-0 h-2 w-2 border-b border-l" style={{
                borderColor: "var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute bottom-0 right-0 h-2 w-2 border-b border-r" style={{
                borderColor: "var(--primary)"
              }} aria-hidden="true" />

                  <div className="mb-3 flex items-center gap-2 font-default text-[11px] font-semibold uppercase" style={{
                letterSpacing: "0.12em",
                color: "var(--muted-foreground)"
              }}>
                    <Activity className="h-3 w-3" strokeWidth={2} style={{
                  color: "var(--accent)"
                }} />
                    <span>{stat.label}</span>
                  </div>
                  <div className="font-default font-bold" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                letterSpacing: "-0.03em",
                color: "var(--primary)",
                lineHeight: "1"
              }}>
                    {stat.value}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 z-10 h-px w-full" style={{
      backgroundColor: "var(--border)"
    }} aria-hidden="true" />

      <style>{`
        @keyframes vanguard-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes vanguard-vertical-scroll {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </section>;
}